import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Sparkle, ClockCounterClockwise, CaretLeft } from "@phosphor-icons/react";
import MarkdownDocument from "@/components/MarkdownDocument";
import MemoActions from "@/components/MemoActions";

// Serialize the structured memo into the same markdown shape the backend
// persists to the diagnostic-memo note, so MemoActions can export a single
// clean document (Situation → Findings → Hypotheses → …).
function buildMemoMarkdown(memo) {
  if (!memo) return "";
  const bulletBlock = (label, items) => {
    const arr = Array.isArray(items) ? items : [];
    if (!arr.length) return "";
    return `## ${label}\n${arr.map((x) => `- ${x}`).join("\n")}\n\n`;
  };
  return (
    `# ${memo.title || "Diagnostic memo"}\n\n` +
    (memo.situation ? `## Situation\n${memo.situation}\n\n` : "") +
    bulletBlock("Key findings", memo.key_findings) +
    bulletBlock("Hypotheses", memo.hypotheses) +
    bulletBlock("Recommendations", memo.recommendations) +
    bulletBlock("Risks & watchouts", memo.risks_and_watchouts) +
    bulletBlock("Next moves this week", memo.next_moves)
  ).trimEnd();
}

// Human-readable relative age for a saved memo — "today", "3d ago", "2w ago".
// Version-history strip only needs quick temporal orientation, so we stop at
// weeks; anything older shows the calendar date.
function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  const day = 86400_000;
  if (ms < day) return "today";
  if (ms < 2 * day) return "1d ago";
  if (ms < 7 * day) return `${Math.floor(ms / day)}d ago`;
  if (ms < 28 * day) return `${Math.floor(ms / (7 * day))}w ago`;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function DiagnosticMemoDialog({ engagementId, open, onOpenChange, onSaved }) {
  const [extra, setExtra] = useState("");
  const [memo, setMemo] = useState(null);
  const [busy, setBusy] = useState(false);
  // Prior drafts of THIS engagement's diagnostic memo, newest first. Populated
  // on open and refreshed each time a new draft is saved.
  const [history, setHistory] = useState([]);
  // When set, we render a prior memo (note.body markdown) read-only inside the
  // same dialog so partners can compare last week's read to today's without
  // navigating away.
  const [viewingHistoryNote, setViewingHistoryNote] = useState(null);

  const loadHistory = async () => {
    if (!engagementId) return;
    try {
      const { data } = await api.get(`/engagements/${engagementId}/diagnostic-memo-history`);
      setHistory(data.history || []);
    } catch (e) {
      // Non-fatal: the dialog still works for fresh drafts if history fetch fails.
      console.warn("Diagnostic memo history load failed", e);
      setHistory([]);
    }
  };

  useEffect(() => {
    if (open) loadHistory();
    // Reset the transient read-only view whenever the dialog is re-opened so
    // partners always land on the intro/draft surface first.
    if (!open) setViewingHistoryNote(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, engagementId]);

  const draft = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/engagements/${engagementId}/generate-diagnostic`, { engagement_id: engagementId, extra_context: extra });
      setMemo(data.memo);
      // Optimistically prepend the freshly saved note so the history strip
      // reflects the new version without a round trip.
      if (data.note) setHistory((prev) => [data.note, ...prev]);
      toast.success("Diagnostic memo drafted & saved as a strategic memo note");
      onSaved?.(data.note);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft failed");
    } finally { setBusy(false); }
  };

  const close = () => {
    setMemo(null);
    setExtra("");
    setViewingHistoryNote(null);
    onOpenChange(false);
  };

  const showIntro = !memo && !viewingHistoryNote;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none bg-card max-w-3xl max-h-[90vh] overflow-auto" data-testid="diagnostic-dialog">
        <DialogDescription className="sr-only">
          Draft a partner-quality diagnostic memo for this engagement, browse prior drafts, and export the result to Word or PDF.
        </DialogDescription>
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Sparkle size={16} weight="fill" className="text-primary" /> Draft diagnostic memo
          </DialogTitle>
        </DialogHeader>

        {showIntro && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">YadidAi reads this engagement + client + recent notes + open tasks + last 14 days of timesheet activity, then writes a partner-quality diagnostic memo (findings, hypotheses, recommendations, risks, next moves).</p>
            <div>
              <label className="text-[0.6875rem] uppercase font-mono tracking-widest text-muted-foreground">Extra context (optional)</label>
              <Textarea rows={4} value={extra} onChange={(e) => setExtra(e.target.value)} placeholder="Anything the AI should weigh — board dynamics, upcoming deadlines, sensitive client feedback…" className="rounded-none bg-secondary/40 mt-1" />
            </div>
            <Button data-testid="diagnostic-draft-btn" onClick={draft} disabled={busy} className="rounded-none bg-primary text-primary-foreground w-full h-11">
              <Sparkle size={14} weight="fill" className="mr-2" /> {busy ? "Drafting memo…" : "Draft diagnostic memo"}
            </Button>

            {history.length > 0 && (
              <div className="pt-3 border-t border-border" data-testid="memo-history">
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-1.5">
                  <ClockCounterClockwise size={11} weight="bold" /> Previous drafts ({history.length})
                </div>
                <ul className="space-y-1.5">
                  {history.map((n) => (
                    <li key={n.id}>
                      <button
                        onClick={() => setViewingHistoryNote(n)}
                        data-testid={`memo-history-row-${n.id}`}
                        className="w-full text-left px-3 py-2 border border-border hover:border-primary/60 hover:bg-secondary/40 transition-colors flex items-start justify-between gap-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{n.title || "Diagnostic memo"}</div>
                          <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                            {n.author_name || "YadidAi"} · {new Date(n.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                          </div>
                        </div>
                        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-primary shrink-0">{relTime(n.created_at)}</div>
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        {viewingHistoryNote && (
          <div className="space-y-4 text-sm" data-testid="memo-history-view">
            <button
              onClick={() => setViewingHistoryNote(null)}
              data-testid="memo-history-back"
              className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground hover:text-primary inline-flex items-center gap-1.5"
            >
              <CaretLeft size={11} weight="bold" /> Back to drafts
            </button>
            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">
                {new Date(viewingHistoryNote.created_at).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" })}
                {viewingHistoryNote.author_name && <span> · {viewingHistoryNote.author_name}</span>}
              </div>
              <h2 className="font-display text-2xl font-bold tracking-tight">{viewingHistoryNote.title}</h2>
            </div>
            <MarkdownDocument>{viewingHistoryNote.body || ""}</MarkdownDocument>
            <MemoActions markdown={viewingHistoryNote.body || ""} title={viewingHistoryNote.title || "Diagnostic memo"} />
          </div>
        )}

        {memo && (
          <div className="space-y-5 text-sm">
            <h2 className="font-display text-2xl font-bold tracking-tight" data-testid="memo-title">{memo.title}</h2>

            <div>
              <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-1">Situation</div>
              <MarkdownDocument>{memo.situation || ""}</MarkdownDocument>
            </div>

            {[
              { key: "key_findings", label: "Key findings" },
              { key: "hypotheses", label: "Hypotheses" },
              { key: "recommendations", label: "Recommendations" },
              { key: "risks_and_watchouts", label: "Risks & watchouts" },
              { key: "next_moves", label: "Next moves this week" },
            ].map(({ key, label }) => (
              <div key={key}>
                <div className="text-[0.625rem] font-mono uppercase tracking-widest text-muted-foreground mb-2">{label}</div>
                <ul className="space-y-1.5">
                  {(memo[key] || []).map((x, i) => (
                    <li key={i} className="pl-3 border-l-2 border-primary/40">
                      <MarkdownDocument className="!space-y-0 [&_p]:!m-0">{x}</MarkdownDocument>
                    </li>
                  ))}
                </ul>
              </div>
            ))}

            <MemoActions markdown={buildMemoMarkdown(memo)} title={memo.title || "Diagnostic memo"} />

            <div className="border-t border-border pt-4 text-xs text-muted-foreground">Saved as a strategic memo note on this engagement.</div>
          </div>
        )}

        <DialogFooter className="gap-2">
          {memo && (
            <>
              <Button onClick={() => { setMemo(null); }} variant="outline" className="rounded-none" data-testid="memo-redraft">
                <Sparkle size={12} className="mr-1" /> Redraft
              </Button>
              <Button onClick={close} className="rounded-none bg-primary text-primary-foreground" data-testid="memo-close">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
